import React, { useState, useEffect } from 'react';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { useClusters, Cluster } from '@/hooks/useClusters';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Plus, Edit, Trash2, Search, RefreshCw, Building2, FileText, HardHat, Users } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { MunicipioAutocomplete } from '@/components/empreendimentos/MunicipioAutocomplete';

interface ClusterMetrics {
  cluster_id: string;
  total_hubs: number;
  total_contratos: number;
  total_obras: number;
  total_profissionais: number;
}

const defaultCluster = {
  name: '',
  color: '#3B82F6',
  sede_municipio_ibge: '' as string,
};

const ClustersPage: React.FC = () => {
  const { clusters, loading, refetch, createCluster, updateCluster, deleteCluster } = useClusters();
  const { canEdit } = useAuth();
  const { toast } = useToast();

  const [searchQuery, setSearchQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCluster, setEditingCluster] = useState<Cluster | null>(null);
  const [formData, setFormData] = useState(defaultCluster);
  const [saving, setSaving] = useState(false);
  const [clusterMetrics, setClusterMetrics] = useState<ClusterMetrics[]>([]);

  // Buscar métricas consolidadas dos clusters
  useEffect(() => {
    const fetchMetrics = async () => {
      const { data, error } = await supabase
        .from('vw_cluster_consolidado')
        .select('*');

      if (!error && data) {
        setClusterMetrics(data.map(d => ({
          cluster_id: d.cluster_id,
          total_hubs: Number(d.total_hubs) || 0,
          total_contratos: Number(d.total_contratos) || 0,
          total_obras: Number(d.total_obras) || 0,
          total_profissionais: Number(d.total_profissionais) || 0,
        })));
      }
    };

    if (clusters.length > 0) {
      fetchMetrics();
    }
  }, [clusters]);

  const getMetricsForCluster = (clusterId: string): ClusterMetrics => {
    return clusterMetrics.find(m => m.cluster_id === clusterId) || {
      cluster_id: clusterId,
      total_hubs: 0,
      total_contratos: 0,
      total_obras: 0,
      total_profissionais: 0,
    };
  };

  const totals = clusterMetrics.reduce((acc, m) => ({
    hubs: acc.hubs + m.total_hubs,
    contratos: acc.contratos + m.total_contratos,
    obras: acc.obras + m.total_obras,
    profissionais: acc.profissionais + m.total_profissionais,
  }), { hubs: 0, contratos: 0, obras: 0, profissionais: 0 });

  const filteredClusters = clusters.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCreate = () => {
    setEditingCluster(null);
    setFormData(defaultCluster);
    setModalOpen(true);
  };

  const handleEdit = (cluster: Cluster) => {
    setEditingCluster(cluster);
    setFormData({
      name: cluster.name,
      color: cluster.color,
      sede_municipio_ibge: cluster.sede_municipio_ibge || '',
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingCluster) {
        await updateCluster(editingCluster.id, formData);
      } else {
        await createCluster(formData);
      }
      setModalOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('Tem certeza que deseja excluir este cluster?')) {
      await deleteCluster(id);
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Buscar clusters..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="icon" onClick={refetch} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          {canEdit && (
            <Button onClick={handleCreate}>
              <Plus className="h-4 w-4 mr-2" />
              Novo Cluster
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10"><Building2 className="h-5 w-5 text-primary" /></div>
            <div>
              <p className="text-2xl font-bold">{clusters.length}</p>
              <p className="text-xs text-muted-foreground">Clusters</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/10"><Building2 className="h-5 w-5 text-blue-500" /></div>
            <div>
              <p className="text-2xl font-bold">{totals.hubs}</p>
              <p className="text-xs text-muted-foreground">Hubs</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/10"><FileText className="h-5 w-5 text-purple-500" /></div>
            <div>
              <p className="text-2xl font-bold">{totals.contratos}</p>
              <p className="text-xs text-muted-foreground">Contratos</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/10"><HardHat className="h-5 w-5 text-amber-500" /></div>
            <div>
              <p className="text-2xl font-bold">{totals.obras}</p>
              <p className="text-xs text-muted-foreground">Obras</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/10"><Users className="h-5 w-5 text-green-500" /></div>
            <div>
              <p className="text-2xl font-bold">{totals.profissionais}</p>
              <p className="text-xs text-muted-foreground">Profissionais</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-4">{[1, 2, 3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cor</TableHead>
                  <TableHead>Nome</TableHead>
                   <TableHead className="text-center">Hubs</TableHead>
                  <TableHead className="text-center">Contratos</TableHead>
                  <TableHead className="text-center">Obras</TableHead>
                  <TableHead className="text-center">Profissionais</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClusters.map((cluster) => {
                  const metrics = getMetricsForCluster(cluster.id);
                  return (
                    <TableRow key={cluster.id}>
                      <TableCell><div className="w-6 h-6 rounded" style={{ backgroundColor: cluster.color }} /></TableCell>
                      <TableCell className="font-medium">{cluster.name}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">{metrics.total_hubs}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">{metrics.total_contratos}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">{metrics.total_obras}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">{metrics.total_profissionais}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {canEdit && (
                          <div className="flex justify-end gap-2">
                            <Button variant="ghost" size="icon-sm" onClick={() => handleEdit(cluster)}><Edit className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="icon-sm" onClick={() => handleDelete(cluster.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filteredClusters.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Nenhum cluster encontrado</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCluster ? 'Editar Cluster' : 'Novo Cluster'}</DialogTitle>
            <DialogDescription>
              Configure o cluster e gerencie os municípios vinculados.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Nome *</Label>
              <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Nome do cluster" />
            </div>
            <div>
              <Label>Cor</Label>
              <div className="flex gap-2 mt-1">
                <Input type="color" value={formData.color} onChange={(e) => setFormData({ ...formData, color: e.target.value })} className="w-16 h-10 p-1" />
                <Input value={formData.color} onChange={(e) => setFormData({ ...formData, color: e.target.value })} placeholder="#000000" />
              </div>
            </div>

            <div>
              <Label>Cidade Sede</Label>
              <MunicipioAutocomplete
                value={formData.sede_municipio_ibge}
                onChange={(codigoIbge) => setFormData(prev => ({ ...prev, sede_municipio_ibge: codigoIbge }))}
                placeholder="Pesquisar cidade sede..."
              />
            </div>

          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving || !formData.name}>
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClustersPage;
